import { useMemo, useState } from 'react';
import {
  ALL_ROLES,
  CAPABILITY_GROUPS,
  LIVE_CAPABILITIES,
  PERMISSION_LEVELS,
  SUPER_ADMIN_ONLY_CAPABILITIES,
  UserRole,
  validatePermissionOverride,
  type Capability,
  type PermissionLevel,
  type PermissionOverride,
} from '@jalupro/shared';
import { api, useApi, useMutation } from '../../api';
import { useAuth } from '../../auth';
import { LoadingState, ErrorState } from '../../components';
import { useT, useRoleLabel, useRoleDescription } from '../../i18n';
import dict, { CAPABILITY_LABELS } from './PermissionsMatrix.i18n';

/**
 * Settings → Permissions (Super Admin), one permission at a time:
 *   1. the roles in the system;
 *   2. a picker — choose a permission, read what it allows;
 *   3. for that permission, the level each role holds (editable), with its default.
 * Levels are the client's own legend (Area 1 §4.3). Edits across several permissions
 * accumulate as a draft and are saved together; the API validates the guard-rails
 * (Super Admin column fixed, users/settings reserved, Agent owner-scoped) and audits.
 */
type CellKey = `${Capability}:${UserRole}`;
const key = (c: Capability, r: UserRole): CellKey => `${c}:${r}`;

const LEVEL_TONE: Record<PermissionLevel, string> = {
  F: 'var(--navy)',
  W: 'var(--s1)',
  R: 'var(--green)',
  U: 'var(--amber)',
  O: 'var(--gold)',
  '-': 'var(--faint)',
};

const levelKey = (l: PermissionLevel) => `level_${l === '-' ? 'none' : l}`;

export function PermissionsMatrix() {
  const t = useT(dict);
  const roleLabel = useRoleLabel();
  const roleDescription = useRoleDescription();
  const { refreshPermissions } = useAuth();
  const matrixReq = useApi(() => api.permissions.matrix(), []);
  const save = useMutation(api.permissions.save);
  const reset = useMutation(api.permissions.reset);
  const [selected, setSelected] = useState<Capability | ''>('');
  const [draft, setDraft] = useState<Record<string, PermissionLevel>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const data = matrixReq.data;
  const effective = useMemo(() => {
    const m = new Map<CellKey, { level: PermissionLevel; defaultLevel: PermissionLevel }>();
    for (const c of data?.cells ?? []) m.set(key(c.capability, c.role), { level: c.level, defaultLevel: c.defaultLevel });
    return m;
  }, [data]);

  const levelOf = (c: Capability, r: UserRole): PermissionLevel =>
    draft[key(c, r)] ?? effective.get(key(c, r))?.level ?? '-';
  const defaultOf = (c: Capability, r: UserRole): PermissionLevel => effective.get(key(c, r))?.defaultLevel ?? '-';

  const changes: PermissionOverride[] = useMemo(
    () =>
      Object.entries(draft)
        .filter(([k, level]) => effective.get(k as CellKey)?.level !== level)
        .map(([k, level]) => {
          const [capability, role] = k.split(':') as [Capability, UserRole];
          return { capability, role, level };
        }),
    [draft, effective],
  );
  const firstError = changes.map(validatePermissionOverride).find((e) => e !== null) ?? null;
  const overrideCount = data?.overrides.length ?? 0;

  function setCell(c: Capability, r: UserRole, level: PermissionLevel) {
    setNotice(null);
    setDraft((d) => ({ ...d, [key(c, r)]: level }));
  }
  function resetRow(c: Capability) {
    setDraft((d) => {
      const next = { ...d };
      for (const r of ALL_ROLES) next[key(c, r)] = defaultOf(c, r);
      return next;
    });
  }

  async function onSave() {
    if (changes.length === 0 || firstError) return;
    try {
      await save.mutate(changes);
      setDraft({});
      setNotice(t('saved', { count: changes.length }));
      await refreshPermissions();
      matrixReq.reload();
    } catch {
      /* save.error surfaced below */
    }
  }
  async function onResetAll() {
    if (!window.confirm(t('reset_all_confirm'))) return;
    try {
      await reset.mutate();
      setDraft({});
      setNotice(t('reset_done'));
      await refreshPermissions();
      matrixReq.reload();
    } catch {
      /* reset.error surfaced below */
    }
  }

  const cap = selected || null;
  const capLabel = cap ? CAPABILITY_LABELS[cap] : null;
  const reserved = !!cap && SUPER_ADMIN_ONLY_CAPABILITIES.includes(cap);
  const rowDirty = !!cap && ALL_ROLES.some((r) => levelOf(cap, r) !== defaultOf(cap, r));

  return (
    <div className="panel" style={{ overflow: 'hidden' }}>
      <div className="row between center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-2)', gap: 12 }}>
        <div className="col">
          <div className="h3">{t('heading')}</div>
          <div className="meta" style={{ fontSize: 12.5 }}>
            {overrideCount === 0 ? t('all_default') : t('override_count', { count: overrideCount })}
            {changes.length > 0 && <> · <b style={{ color: 'var(--gold)' }}>{t('unsaved', { count: changes.length })}</b></>}
          </div>
        </div>
        <div className="row gap-8 center">
          <button type="button" className="btn sm" onClick={onResetAll} disabled={reset.loading || overrideCount === 0}>
            {t('reset_all')}
          </button>
          <button
            type="button"
            className="btn sm primary"
            onClick={onSave}
            disabled={save.loading || changes.length === 0 || !!firstError}
          >
            {save.loading ? t('saving') : t('save')}
          </button>
        </div>
      </div>

      <div className="col gap-16" style={{ padding: 16 }}>
        <div className="meta" style={{ fontSize: 13, lineHeight: 1.5 }}>{t('intro')}</div>

        {/* 1 · Roles */}
        <div className="col gap-6">
          <div className="eyebrow">{t('roles_heading')}</div>
          <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
            {ALL_ROLES.map((r) => (
              <span key={r} className="pill" title={roleDescription(r)} style={{ fontSize: 12.5 }}>
                {roleLabel(r)}
              </span>
            ))}
          </div>
        </div>

        {/* 2 · Permission picker */}
        <label className="col gap-4" style={{ maxWidth: 520 }}>
          <span className="label">{t('pick_label')}</span>
          <select className="input" value={selected} onChange={(e) => setSelected(e.target.value as Capability | '')}>
            <option value="">{t('pick_placeholder')}</option>
            {/* Only permissions that gate something reachable today (LIVE_CAPABILITIES);
                the rest of the matrix waits for its module. */}
            {CAPABILITY_GROUPS.map((g) => ({ ...g, capabilities: g.capabilities.filter((c) => LIVE_CAPABILITIES.includes(c)) }))
              .filter((g) => g.capabilities.length > 0)
              .map((g) => (
                <optgroup key={g.key} label={t(`group_${g.key}`)}>
                  {g.capabilities.map((c) => (
                    <option key={c} value={c}>
                      {CAPABILITY_LABELS[c].en}
                    </option>
                  ))}
                </optgroup>
              ))}
          </select>
        </label>

        {firstError && <ErrorState message={firstError} />}
        {save.error && <ErrorState message={save.error.message} />}
        {reset.error && <ErrorState message={reset.error.message} />}
        {notice && <div className="meta" style={{ color: 'var(--green)', fontSize: 13 }}>{notice}</div>}

        {matrixReq.loading ? (
          <LoadingState label={t('loading')} />
        ) : matrixReq.error ? (
          <ErrorState message={matrixReq.error.message} onRetry={matrixReq.reload} />
        ) : cap && capLabel ? (
          <div className="col gap-12">
            {/* 3 · The chosen permission */}
            <div className="note">
              <b>{capLabel.en}</b> <span className="mono meta" style={{ fontSize: 11 }}>{cap}</span>
              <div style={{ marginTop: 4 }}>{capLabel.desc}</div>
              {reserved && <div className="meta" style={{ marginTop: 4 }}>{t('locked_reserved')}</div>}
            </div>

            <table style={{ maxWidth: 760 }}>
              <thead>
                <tr>
                  <th>{t('col_role')}</th>
                  <th style={{ width: 300 }}>{t('col_level')}</th>
                  <th style={{ width: 110 }}>{t('col_default')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ALL_ROLES.map((r) => {
                  const level = levelOf(cap, r);
                  const def = defaultOf(cap, r);
                  const locked = r === UserRole.SUPER_ADMIN || reserved;
                  const dirty = level !== def;
                  return (
                    <tr key={r}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{roleLabel(r)}</div>
                        <div className="meta" style={{ fontSize: 11.5, whiteSpace: 'normal', maxWidth: 320 }}>{roleDescription(r)}</div>
                      </td>
                      <td>
                        <select
                          className="input"
                          value={level}
                          disabled={locked}
                          aria-label={`${cap} · ${r}`}
                          onChange={(e) => setCell(cap, r, e.target.value as PermissionLevel)}
                          style={{
                            width: '100%',
                            fontWeight: 600,
                            color: LEVEL_TONE[level],
                            borderColor: dirty ? 'var(--gold)' : undefined,
                            background: dirty ? 'var(--gold-bg)' : locked ? 'var(--card-2)' : undefined,
                            cursor: locked ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {PERMISSION_LEVELS.map((l) => (
                            <option key={l} value={l}>
                              {l === '-' ? '—' : l} · {t(levelKey(l))}
                            </option>
                          ))}
                        </select>
                        {r === UserRole.SUPER_ADMIN && <div className="meta" style={{ fontSize: 11.5 }}>{t('locked_sa')}</div>}
                        {r === UserRole.AGENT && !reserved && <div className="meta" style={{ fontSize: 11.5 }}>{t('agent_rule')}</div>}
                      </td>
                      <td className="mono" style={{ color: LEVEL_TONE[def] }}>
                        {def === '-' ? '—' : def} <span className="meta" style={{ fontFamily: 'inherit' }}>· {t(levelKey(def))}</span>
                      </td>
                      <td />
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {rowDirty && (
              <div>
                <button type="button" className="btn sm" onClick={() => resetRow(cap)}>
                  ↺ {t('reset_row')}
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
